import { ReactNode } from 'react'
import {
  AlertDialog as AlertDialogCore,
  GetProps,
  XStack,
  YStack,
} from 'tamagui'
import { Button, ButtonProps } from './Button'

export type AlertDialogProps = GetProps<typeof AlertDialogCore> & {
  trigger: ReactNode
  title: ReactNode
  description: ReactNode
  onCancelPress?: () => void
  onSubmitPress: () => void
  isLoading?: boolean
  cancelText?: ReactNode
  submitText?: ReactNode
  acceptEvent: ButtonProps['event']
}

export const AlertDialog = ({
  trigger,
  title,
  description,
  onCancelPress,
  onSubmitPress,
  cancelText,
  submitText,
  acceptEvent,
  isLoading,
  ...props
}: AlertDialogProps) => {
  return (
    <AlertDialogCore {...props}>
      <AlertDialogCore.Trigger asChild>{trigger}</AlertDialogCore.Trigger>

      <AlertDialogCore.Portal>
        <AlertDialogCore.Overlay
          key="overlay"
          animation="quick"
          o={1}
          shadowOpacity={0}
          backgroundColor="$blackAlpha.600"
          enterStyle={{ o: 0 }}
          exitStyle={{ o: 0 }}
        />
        <AlertDialogCore.Content
          elevate
          key="content"
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          p="$6"
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          opacity={1}
          y={0}
        >
          <YStack space="$6">
            <YStack space="$2">
              <AlertDialogCore.Title size="$xl">{title}</AlertDialogCore.Title>
              <AlertDialogCore.Description color="$neutral.600">
                {description}
              </AlertDialogCore.Description>
            </YStack>

            <XStack space="$3" jc="space-evenly">
              <AlertDialogCore.Cancel asChild>
                <Button
                  flex={1}
                  variant="outline"
                  colorScheme="neutral"
                  size="$lg"
                  onPress={onCancelPress}
                  event="Alert Dialog Cancel Pressed"
                >
                  {cancelText ?? 'Cancel'}
                </Button>
              </AlertDialogCore.Cancel>
              <AlertDialogCore.Action asChild>
                <Button
                  event={acceptEvent}
                  flex={1}
                  size="$lg"
                  isLoading={isLoading}
                  onPress={onSubmitPress}
                >
                  {submitText ?? 'Accept'}
                </Button>
              </AlertDialogCore.Action>
            </XStack>
          </YStack>
        </AlertDialogCore.Content>
      </AlertDialogCore.Portal>
    </AlertDialogCore>
  )
}
